import React, { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { PageType } from '~/api/generated/graphql'
import { getApiBase } from '~/api/ServerApi'
import { AuthStatus } from '~/api/types'
import { RequireAuth } from '~/auth/Auth'
import { RequireCommunities } from '~/contexts/CommunitiesContext'
import { RequireTypeahead } from '~/contexts/TypeaheadContext'
import Version from '~/pages/Version'
import { LocalStorageKey, PageAction } from '~/types'
import { ToastContainer } from 'react-bootstrap'
import CommunityListLayout, { CommunityListTab } from '~/pages/community/CommunityListLayout'
import HomeFeed, { HomeFeedTab } from '~/pages/feed/HomeFeed'

const CommunityLayout = lazy(() => import('~/pages/community/CommunityLayout'))
const CommunityAbout = lazy(() => import('./pages/community/CommunityAbout'))
const CommunityMembers = lazy(() => import('~/pages/community/CommunityMembers'))
const CommunityPosts = lazy(() => import('~/pages/community/CommunityPosts'))
const CommunityPost = lazy(() => import('~/pages/community/CommunityPost'))
const Login = lazy(() => import('~/pages/Login').then(({ Login }) => ({ default: Login })))
const Privacy = lazy(() => import('~/pages/Privacy').then(({ Privacy }) => ({ default: Privacy })))
const Terms = lazy(() => import('~/pages/Terms').then(({ Terms }) => ({ default: Terms })))
const CommunityEvents = lazy(() => import('~/pages/community/events/CommunityEvents'))
const CommunityContent = lazy(() => import('~/pages/community/CommunityContent'))
const ProfileLayout = lazy(() =>
  import('~/pages/profile/ProfileLayout').then(({ ProfileLayout }) => ({ default: ProfileLayout }))
)
const CompanyLayout = lazy(() => import('~/pages/company/CompanyLayout'))
const CompanyAccountPartners = lazy(() => import('~/pages/company/CompanyAccountPartners'))
const CompanyHome = lazy(() =>
  import('~/pages/company/CompanyHome').then(({ CompanyHome }) => ({ default: CompanyHome }))
)
const SettingsLayout = lazy(() => import('~/pages/profile/SettingsLayout'))
const CommunityHistory = lazy(() => import('~/pages/community/CommunityHistory'))
const CustomerActivity = lazy(() =>
  import('~/pages/company/CustomerActivity').then(({ CustomerActivity }) => ({ default: CustomerActivity }))
)
const CommunitySettings = lazy(() => import('~/pages/community/CommunitySettings'))
const PostRepostOverlay = lazy(() => import('~/pages/posts/PostRepostOverlay'))
const EventRepostOverlay = lazy(() => import('~/pages/community/events/EventRepostOverlay'))
const PageLayout = lazy(() => import('~/pages/page/PageLayout'))
const AddMenuPageWrapper = lazy(() => import('~/pages/page/AddPage'))
const AdvancedSearch = lazy(() =>
  import('~/pages/search/AdvancedSearch').then(({ AdvancedSearch }) => ({ default: AdvancedSearch }))
)
const CustomLink = lazy(() => import('~/pages/CustomLink').then(({ CustomLink }) => ({ default: CustomLink })))
const CommunityEvent = lazy(() =>
  import('~/pages/community/events/CommunityEvent').then(({ CommunityEvent }) => ({ default: CommunityEvent }))
)
const SummitLivestreams = lazy(() =>
  import('~/pages/page/SummitLivestreams').then(({ SummitLivestreams }) => ({ default: SummitLivestreams }))
)
const SummitLivestreamEvents = lazy(() =>
  import('~/pages/page/SummitLivestreamEvents').then(({ SummitLivestreamEvents }) => ({
    default: SummitLivestreamEvents,
  }))
)
const LoginBackground = lazy(() => import('~/pages/LoginBackground'))
const FreeSiteSettings = lazy(() => import('~/pages/company/FreeSiteSettings'))
const CompanySettings = lazy(() =>
  import('~/pages/company/CompanySettings').then(({ CompanySettings }) => ({ default: CompanySettings }))
)

let PROMOTED_APP_VERSION = ''
export const set_promoted_app_version = (version: string) => (PROMOTED_APP_VERSION = version)
export const App = () => {
  const API_BASE = getApiBase()
  const APP_VERSION = JSON.parse(document.getElementById('data/VERSION')?.textContent ?? '0')
  const API_HOSTNAME = API_BASE ? new URL(API_BASE).hostname : ''
  const summitPaths = ['summit', 'summits']

  const location = useLocation()

  // A successful mount means we're past whatever crashed last time (see RouteError in web.tsx) -
  // clear the guard so a future chunk-load failure gets one fresh auto-reload attempt.
  useEffect(() => {
    sessionStorage.removeItem(LocalStorageKey.chunkReload)
  }, [])

  // Keep track of the previous path in order to check if we just came from /communities/* to persist scroll height in the communities list
  useEffect(() => {
    let from = localStorage.getItem(LocalStorageKey.current) ?? ''
    let current = location.pathname

    const listNames = new Set(['/communities', '/mycommunities', '/allcommunities'])

    // We want to treat /about /posts etc. pages to all be just /communities/id/
    const fromMatch = from.match(/^\/(communities|companies)\/[^/]+/)
    from = fromMatch ? (fromMatch[0] ?? from) : from
    const currentMatch = current.match(/^\/(communities|companies)\/[^/]+/)
    current = currentMatch ? (currentMatch[0] ?? current) : current

    if (!(currentMatch && currentMatch[0] && from === current)) {
      localStorage.setItem(LocalStorageKey.from, from)
      localStorage.setItem(LocalStorageKey.current, current)

      // If we aren't going between the list / community pages then we can reset the scroll/pagination state
      if (!(fromMatch && fromMatch[0] && listNames.has(current)) && !(currentMatch && listNames.has(from))) {
        localStorage.setItem(LocalStorageKey.displayedCountMine, '25')
        localStorage.setItem(LocalStorageKey.displayedCountAll, '25')
        localStorage.setItem(LocalStorageKey.communitiesScrollHeight, '0')
      }
    }
  }, [location.pathname])

  useEffect(() => {
    if (
      PROMOTED_APP_VERSION &&
      PROMOTED_APP_VERSION > APP_VERSION &&
      API_HOSTNAME &&
      API_HOSTNAME.toLowerCase() !== globalThis.location.hostname.toLowerCase()
    ) {
      globalThis.location.reload()
    }
    // We want to preserve scroll position on release/summit pages.
    // We also don't want to scroll if we are scrolling to a comment or event
    if (!(
      location.pathname.includes('release') ||
      location.pathname.includes('summits') ||
      location.pathname.includes('search') ||
      location.pathname === '/communities' ||
      location.pathname === '/mycommunities' ||
      location.pathname === '/allcommunities' ||
      location.pathname === '/recommendedcommunities' ||
      location.pathname === '/veeva-onlycommunities' ||
      location.search.includes('c=') ||
      location.search.includes('e=')
    )) {
      document.querySelector('.main')?.scroll(0, 0)
    }
  }, [location, APP_VERSION, API_HOSTNAME])
  return (
    <div>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/">
            <Route path="login">
              <Route element={<LoginBackground />}>
                <Route index element={<Login initialAuthStatus={AuthStatus.initial} />} />
                <Route path="complete" element={<Login showCompleteProfile={true} />} />
                <Route path="communities" element={<Login showRegistrationCommunities={true} />} />
                <Route path="bad" element={<Login />} />
              </Route>
            </Route>
            <Route path="privacy-policy" element={<Privacy />} />
            <Route path="terms" element={<Terms />} />
            <Route element={<RequireAuth />}>
              <Route path="link/*" element={<CustomLink />} />
              <Route element={<RequireTypeahead />}>
                <Route path="addfreesite" element={<FreeSiteSettings creatingFreeSite={true} />} />
                <Route element={<RequireCommunities />}>
                  <Route index element={<HomeFeed />} />
                  <Route path="feed" element={<HomeFeed />} />
                  <Route path="myfeed" element={<HomeFeed homeFeedTab={HomeFeedTab.My} />} />
                  <Route path="followingfeed" element={<HomeFeed homeFeedTab={HomeFeedTab.Following} />} />
                  <Route path="veevaonlyfeed" element={<HomeFeed homeFeedTab={HomeFeedTab.VeevaOnly} />} />
                  <Route path="customerfeed" element={<HomeFeed homeFeedTab={HomeFeedTab.Customer} />} />
                  <Route path="recommendedcommunities">
                    <Route index element={<CommunityListLayout communityListTab={CommunityListTab.Recommended} />} />
                  </Route>
                  <Route path="veeva-onlycommunities">
                    <Route index element={<CommunityListLayout communityListTab={CommunityListTab.VeevaOnly} />} />
                  </Route>
                  <Route path="mycommunities">
                    <Route index element={<CommunityListLayout communityListTab={CommunityListTab.My} />} />
                  </Route>
                  <Route path="allcommunities">
                    <Route index element={<CommunityListLayout communityListTab={CommunityListTab.All} />} />
                  </Route>
                  <Route path="communities">
                    <Route index element={<CommunityListLayout />} />
                    <Route path=":communityId">
                      <Route element={<CommunityLayout />}>
                        <Route index element={<Navigate to="about" replace />} />
                        <Route path="about" element={<CommunityAbout />} />
                        <Route path="posts">
                          <Route index element={<CommunityPosts />} />
                          <Route path=":postId">
                            <Route index element={<CommunityPost fromContentPage={false} />} />
                            <Route path="repost">
                              <Route index element={<PostRepostOverlay fromContentPage={false} />} />
                            </Route>
                          </Route>
                        </Route>
                        <Route path="members" element={<CommunityMembers isCompany={false} />} />
                        <Route path="events">
                          <Route index element={<CommunityEvents isCompany={false} />} />
                          <Route path=":eventId">
                            <Route index element={<CommunityEvent />} />
                            <Route path="repost">
                              <Route index element={<EventRepostOverlay />} />
                            </Route>
                          </Route>
                        </Route>
                        <Route path="content">
                          <Route index element={<CommunityContent />} />
                          <Route path=":postId">
                            <Route index element={<CommunityPost fromContentPage={true} />} />
                            <Route path="repost">
                              <Route index element={<PostRepostOverlay fromContentPage={true} />} />
                            </Route>
                          </Route>
                        </Route>
                        <Route path="settings" element={<CommunitySettings />} />
                        <Route path="history" element={<CommunityHistory isCompany={false} />} />
                      </Route>
                    </Route>
                  </Route>
                  <Route path="profiles">
                    <Route path=":userId" element={<ProfileLayout isEditing={false} />} />
                    <Route path=":userId">
                      <Route path="edit" element={<ProfileLayout isEditing={true} />} />
                    </Route>
                  </Route>
                  <Route
                    path="edit-my-profile"
                    element={<ProfileLayout isEditing={true} sendToProfile={true} />}
                  ></Route>
                  <Route path="search" element={<AdvancedSearch />} />
                  <Route path="companies">
                    <Route path=":companyId" element={<CompanyLayout />}>
                      <Route index element={<Navigate to={'home'} replace />} />
                      <Route path="home" element={<CompanyHome />} />
                      <Route path="posts">
                        <Route index element={<CommunityPosts />} />
                        <Route path=":postId" element={<CommunityPost fromContentPage={false} />} />
                      </Route>
                      <Route path="comments">
                        <Route index element={<CommunityPosts />} />
                        <Route path=":commentId" element={<CommunityPost fromContentPage={false} />} />
                      </Route>
                      <Route path="content">
                        <Route index element={<CommunityContent />} />
                        <Route path=":postId" element={<CommunityPost fromContentPage={true} />} />
                      </Route>
                      <Route path="partners" element={<CompanyAccountPartners />} />
                      <Route path="members" element={<CommunityMembers isCompany={true} />} />
                      <Route path="events">
                        <Route index element={<CommunityEvents isCompany={true} />} />
                        <Route path=":eventId">
                          <Route index element={<CommunityEvent />} />
                        </Route>
                      </Route>
                      <Route path="settings" element={<CompanySettings />} />
                      <Route path="history" element={<CommunityHistory isCompany={true} />} />
                      <Route path="activity" element={<CustomerActivity />} />
                    </Route>
                  </Route>
                  <Route path="releases">
                    <Route path=":releaseId" element={<PageLayout pageType={PageType.Release} />} />
                  </Route>
                  {summitPaths.map(s => (
                    <Route path={s} key={s}>
                      <Route path=":releaseId" element={<PageLayout pageType={PageType.Summit} />} />
                    </Route>
                  ))}
                </Route>
                <Route
                  path="addreleasepage"
                  element={<AddMenuPageWrapper pageAction={PageAction.Add} pageType={PageType.Release} />}
                />
                <Route path="releases">
                  <Route path=":releaseId">
                    <Route
                      path="edit"
                      element={<AddMenuPageWrapper pageAction={PageAction.Edit} pageType={PageType.Release} />}
                    />
                    <Route
                      path="clone"
                      element={<AddMenuPageWrapper pageAction={PageAction.Clone} pageType={PageType.Release} />}
                    />
                  </Route>
                </Route>
                <Route
                  path="addsummitpage"
                  element={<AddMenuPageWrapper pageAction={PageAction.Add} pageType={PageType.Summit} />}
                />
                {summitPaths.map(s => (
                  <Route path={s} key={s}>
                    <Route path=":releaseId">
                      <Route
                        path="edit"
                        element={<AddMenuPageWrapper pageAction={PageAction.Edit} pageType={PageType.Summit} />}
                      />
                      <Route path="livestream" element={<SummitLivestreams />} />
                      <Route path="livestreamEvents" element={<SummitLivestreamEvents />} />
                    </Route>
                  </Route>
                ))}
                <Route path="settings" element={<SettingsLayout />} />
              </Route>
            </Route>
            <Route path="version" element={<Version />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
      <ToastContainer position="top-end" id="toast-container" />
    </div>
  )
}
